
import React, { useState } from 'react';
import SectionSelector from './SectionSelector';
import FormInput from './components/FormInput';
import FormTextarea from './components/FormTextarea';
import AddButton from './components/AddButton';
import RemoveButton from './components/RemoveButton';
import WebsiteLinksInput from './components/WebsiteLinksInput';

const BusinessDevResumeForm = ({ resumeData, onUpdate, validationErrors = {} }) => {
  const [activeSection, setActiveSection] = useState('contact');
  const [newSkill, setNewSkill] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [newWebsite, setNewWebsite] = useState("");
  const [newProject, setNewProject] = useState({ title: "", description: "", link: "", year: "", technologies: "" });

  const updateField = (field, data) => {
    onUpdate(prev => ({ ...prev, [field]: data }));
  };

  const handlePersonalChange = (field, value) => {
    onUpdate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const result = await response.json();
        handlePersonalChange('profile_image_url', result.file_url || URL.createObjectURL(file));
      } else {
        handlePersonalChange('profile_image_url', URL.createObjectURL(file));
      }
    } catch (error) {
      handlePersonalChange('profile_image_url', URL.createObjectURL(file));
    }
    setIsUploading(false);
  };

  const addSkill = () => {
    if (newSkill.trim()) {
      const currentSkills = resumeData.key_skills || [];
      updateField("key_skills", [...currentSkills, newSkill.trim()]);
      setNewSkill("");
    }
  };

  const removeSkill = (index) => {
    const currentSkills = resumeData.key_skills || [];
    updateField("key_skills", currentSkills.filter((_, i) => i !== index));
  };

  const addExperience = () => {
    updateField("relevant_experience", [...(resumeData.relevant_experience || []), {
      position: "",
      company: "",
      start_date: "",
      end_date: "",
      achievements: [""]
    }]);
  };

  const updateExperience = (index, field, value) => {
    const updated = [...(resumeData.relevant_experience || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("relevant_experience", updated);
  };

  const addAchievement = (expIndex) => {
    const updated = [...(resumeData.relevant_experience || [])];
    updated[expIndex].achievements.push("");
    updateField("relevant_experience", updated);
  };

  const updateAchievement = (expIndex, pointIndex, value) => {
    const updated = [...(resumeData.relevant_experience || [])];
    updated[expIndex].achievements[pointIndex] = value;
    updateField("relevant_experience", updated);
  };

  const removeAchievement = (expIndex, pointIndex) => {
    const updated = [...(resumeData.relevant_experience || [])];
    updated[expIndex].achievements.splice(pointIndex, 1);
    updateField("relevant_experience", updated);
  };

  const removeExperience = (index) => {
    updateField("relevant_experience", resumeData.relevant_experience.filter((_, i) => i !== index));
  };

  const addEducation = () => {
    updateField("education", [...(resumeData.education || []), {
      degree: "",
      institution: "",
      gpa: "",
      graduation_date: ""
    }]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...(resumeData.education || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("education", updated);
  };

  const removeEducation = (index) => {
    updateField("education", resumeData.education.filter((_, i) => i !== index));
  };

  const addTraining = () => {
    updateField("training", [...(resumeData.training || []), {
      title: "",
      institution: "",
      year: ""
    }]);
  };

  const updateTraining = (index, field, value) => {
    const updated = [...(resumeData.training || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("training", updated);
  };

  const removeTraining = (index) => {
    updateField("training", resumeData.training.filter((_, i) => i !== index));
  };

  const addReference = () => {
    updateField("references", [...(resumeData.references || []), {
      name: "",
      designation: "",
      company: "",
      email: "",
      phone: ""
    }]);
  };

  const updateReference = (index, field, value) => {
    const updated = [...(resumeData.references || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("references", updated);
  };

  const removeReference = (index) => {
    updateField("references", resumeData.references.filter((_, i) => i !== index));
  };

  const addWebsite = () => {
    if (newWebsite.trim()) {
      updateField("websites", [...(resumeData.websites || []), newWebsite.trim()]);
      setNewWebsite("");
    }
  };
  const removeWebsite = (index) => {
    updateField("websites", resumeData.websites.filter((_, i) => i !== index));
  };
  const addProject = () => {
    if (newProject.title.trim()) {
      const projectData = { 
        ...newProject, 
        technologies: newProject.technologies.split(',').map(t => t.trim()),
        tech_stack: newProject.technologies.split(',').map(t => t.trim()), // Add tech_stack for compatibility
        github_link: newProject.link // Add github_link for compatibility
      };
      updateField("projects", [...(resumeData.projects || []), projectData]);
      setNewProject({ title: "", description: "", link: "", year: "", technologies: "" });
    }
  };
  const removeProject = (index) => {
    updateField("projects", resumeData.projects.filter((_, i) => i !== index));
  };
  const updateProject = (index, field, value) => {
    const updated = [...(resumeData.projects || [])];
    updated[index] = { ...updated[index], [field]: value };
    
    // Maintain data consistency across templates
    if (field === 'technologies') {
      updated[index].tech_stack = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'link') {
      updated[index].github_link = value;
    }
    
    updateField("projects", updated);
  };

  const addLanguage = () => {
    updateField("languages", [...(resumeData.languages || []), {
      name: "",
      proficiency: 50
    }]);
  };

  const updateLanguage = (index, field, value) => {
    const updated = [...(resumeData.languages || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("languages", updated);
  };

  const removeLanguage = (index) => {
    updateField("languages", resumeData.languages.filter((_, i) => i !== index));
  };

  const sections = [
    { id: 'contact', label: 'Contact' },
    { id: 'summary', label: 'Summary' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'education', label: 'Education' },
    { id: 'training', label: 'Training' },
    { id: 'projects', label: 'Projects' },
    { id: 'languages', label: 'Languages' },
    { id: 'references', label: 'References' }
  ];

  return (
    <div className="resume-form" style={{marginTop: 0, paddingTop: 0}}>
      <div className="form-content" style={{marginTop: 0, paddingTop: 0}}>
        <div className="form-header">
          <h2 className="form-title">Business Development Resume Editor</h2>
        </div>
        <SectionSelector
          sections={sections}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        <div className="section-form">
          {activeSection === 'contact' && (
            <div className="tab-panel">
              <h3 className="section-title">Contact Information & Profile</h3>
              <div className="form-group">
                <label>Profile Picture</label>
                <div className="flex items-center gap-4">
                  {resumeData?.profile_image_url && (
                    <img src={resumeData.profile_image_url} alt="Profile" className="w-20 h-20 rounded-full object-cover" />
                  )}
                  <label className="upload-btn">
                    {isUploading ? 'Uploading...' : 'Upload Photo'}
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleImageUpload}
                      accept="image/*"
                      disabled={isUploading}
                    />
                  </label>
                </div>
              </div>
              <div className="form-group-grid">
                <div className={`form-group ${validationErrors.full_name ? 'error' : ''}`}>
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={resumeData?.full_name || ""}
                    onChange={(e) => handlePersonalChange("full_name", e.target.value)}
                    placeholder="John Doe"
                  />
                  {validationErrors.full_name && <p className="error-message">{validationErrors.full_name}</p>}
                </div>
                <div className="form-group">
                  <label>Job Title</label>
                  <input
                    type="text"
                    value={resumeData?.job_title || ""}
                    onChange={(e) => handlePersonalChange("job_title", e.target.value)}
                    placeholder="Business Development Professional"
                  />
                </div>
              </div>
              <div className="form-group-grid">
                <div className={`form-group ${validationErrors.email ? 'error' : ''}`}>
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={resumeData?.email || ""}
                    onChange={(e) => handlePersonalChange("email", e.target.value)}
                    placeholder="thecvguy@gmail.com"
                  />
                  {validationErrors.email && <p className="error-message">{validationErrors.email}</p>}
                </div>
                <div className={`form-group ${validationErrors.phone ? 'error' : ''}`}>
                  <label>Phone Number</label>
                  <input
                    type="text"
                    value={resumeData?.phone || ""}
                    onChange={(e) => handlePersonalChange("phone", e.target.value)}
                    placeholder="+88 01817110000"
                  />
                  {validationErrors.phone && <p className="error-message">{validationErrors.phone}</p>}
                </div>
              </div>
              <div className="form-group-grid">
                <div className="form-group">
                  <label>Website</label>
                  <input
                    type="text"
                    value={resumeData?.websites?.[0] || ""}
                    onChange={(e) => handlePersonalChange("websites", [e.target.value])}
                    placeholder="in/thecvguy.net/"
                    maxLength="50"
                  />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={resumeData?.location || ""}
                    onChange={(e) => handlePersonalChange("location", e.target.value)}
                    placeholder="Dhaka, Bangladesh"
                    maxLength="50"
                  />
                </div>
              </div>
              <WebsiteLinksInput
                websites={resumeData.websites || []}
                newWebsite={newWebsite}
                setNewWebsite={setNewWebsite}
                onAdd={addWebsite}
                onRemove={removeWebsite}
              />
            </div>
          )}

          {activeSection === 'summary' && (
            <div className="tab-panel">
              <h3 className="section-title">Professional Summary</h3>
              <div className={`form-group ${validationErrors.summary ? 'error' : ''}`}>
                <label>Summary</label>
                <textarea
                  value={resumeData?.professional_summary || ""}
                  onChange={(e) => handlePersonalChange("professional_summary", e.target.value)}
                  placeholder="A highly motivated Business Development professional with X years of experience..."
                  rows="5"
                  maxLength="1000"
                ></textarea>
                {validationErrors.summary && <p className="error-message">{validationErrors.summary}</p>}
              </div>
            </div>
          )}

          {activeSection === 'experience' && (
            <div className="tab-panel">
              <h3 className="section-title">Work Experience</h3>
              {(resumeData.relevant_experience || []).map((exp, expIndex) => (
                <div key={expIndex} className="experience-item form-section-item">
                  <div className="form-group-grid">
                    <div className={`form-group ${validationErrors.relevant_experience?.[expIndex]?.position ? 'error' : ''}`}>
                      <label>Position</label>
                      <input
                        type="text"
                        value={exp.position || ""}
                        onChange={(e) => updateExperience(expIndex, "position", e.target.value)}
                        placeholder="Senior Business Development Manager"
                        maxLength="100"
                      />
                      {validationErrors.relevant_experience?.[expIndex]?.position && <p className="error-message">{validationErrors.relevant_experience[expIndex].position}</p>}
                    </div>
                    <div className={`form-group ${validationErrors.relevant_experience?.[expIndex]?.company ? 'error' : ''}`}>
                      <label>Company</label>
                      <input
                        type="text"
                        value={exp.company || ""}
                        onChange={(e) => updateExperience(expIndex, "company", e.target.value)}
                        placeholder="Tech Solutions Inc."
                        maxLength="100"
                      />
                      {validationErrors.relevant_experience?.[expIndex]?.company && <p className="error-message">{validationErrors.relevant_experience[expIndex].company}</p>}
                    </div>
                  </div>
                  <div className="form-group-grid">
                    <div className={`form-group ${validationErrors.relevant_experience?.[expIndex]?.start_date ? 'error' : ''}`}>
                      <label>Start Date</label>
                      <input
                        type="month"
                        value={exp.start_date || ""}
                        onChange={(e) => updateExperience(expIndex, "start_date", e.target.value)}
                      />
                      {validationErrors.relevant_experience?.[expIndex]?.start_date && <p className="error-message">{validationErrors.relevant_experience[expIndex].start_date}</p>}
                    </div>
                    <div className={`form-group ${validationErrors.relevant_experience?.[expIndex]?.end_date ? 'error' : ''}`}>
                      <label>End Date (or 'Present')</label>
                      <input
                        type="month"
                        value={exp.end_date || ""}
                        onChange={(e) => updateExperience(expIndex, "end_date", e.target.value)}
                      />
                      {validationErrors.relevant_experience?.[expIndex]?.end_date && <p className="error-message">{validationErrors.relevant_experience[expIndex].end_date}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Key Achievements</label>
                    {(exp.achievements || []).map((achievement, pointIndex) => (
                      <div key={pointIndex} className="flex items-center gap-2 mb-2">
                        <input
                          type="text"
                          value={achievement || ""}
                          onChange={(e) => updateAchievement(expIndex, pointIndex, e.target.value)}
                          placeholder="Increased sales by 20% in Q4"
                          maxLength="200"
                          className={`flex-grow ${validationErrors.relevant_experience?.[expIndex]?.achievements?.[pointIndex] ? 'input-error' : ''}`}
                        />
                        <button type="button" onClick={() => removeAchievement(expIndex, pointIndex)} className="remove-btn">
                          Remove
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={() => addAchievement(expIndex)} className="add-btn-small">
                      Add Achievement
                    </button>
                    {validationErrors.relevant_experience?.[expIndex]?.achievements && typeof validationErrors.relevant_experience[expIndex].achievements === 'string' && (
                      <p className="error-message">{validationErrors.relevant_experience[expIndex].achievements}</p>
                    )}
                  </div>
                  <button type="button" onClick={() => removeExperience(expIndex)} className="remove-section-btn">
                    Remove Experience
                  </button>
                </div>
              ))}
              <button type="button" onClick={addExperience} className="add-section-btn">
                Add Work Experience
              </button>
            </div>
          )}

          {activeSection === 'skills' && (
            <div className="tab-panel">
              <h3 className="section-title">Skills</h3>
              <div className="skill-category-group mb-6">
                <label>Key Skills</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                    placeholder="Add a new skill"
                    className={`flex-grow ${validationErrors.newSkill && activeSection === 'skills' ? 'input-error' : ''}`}
                    maxLength="50"
                  />
                  <button type="button" onClick={() => addSkill()} className="add-btn-small">
                    Add
                  </button>
                </div>
                {validationErrors.newSkill && activeSection === 'skills' && <p className="error-message">{validationErrors.newSkill}</p>}
                <div className="skills-list flex flex-wrap gap-2">
                  {(resumeData.key_skills || []).map((skill, index) => (
                    <span key={index} className="skill-tag">
                      {skill}
                      <button type="button" onClick={() => removeSkill(index)} className="remove-skill-btn">
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'projects' && (
            <div className="tab-panel">
              <h3 className="section-title">Projects</h3>
              <div className="form-group">
                <label>Title</label>
                <input type="text" value={newProject.title} onChange={e => setNewProject({ ...newProject, title: e.target.value })} placeholder="Project Title" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={newProject.description} onChange={e => setNewProject({ ...newProject, description: e.target.value })} placeholder="Project Description" rows={2} />
              </div>
              <div className="form-group">
                <label>Link</label>
                <input type="text" value={newProject.link} onChange={e => setNewProject({ ...newProject, link: e.target.value })} placeholder="https://github.com/yourproject" />
              </div>
              <div className="form-group">
                <label>Year</label>
                <input type="text" value={newProject.year} onChange={e => setNewProject({ ...newProject, year: e.target.value })} placeholder="2025" />
              </div>
              <div className="form-group">
                <label>Technologies (comma separated)</label>
                <input type="text" value={newProject.technologies} onChange={e => setNewProject({ ...newProject, technologies: e.target.value })} placeholder="React, Node.js, MongoDB" />
              </div>
              <button type="button" onClick={addProject} className="add-btn-small">Add Project</button>
              <div className="skills-list flex flex-wrap gap-2" style={{ marginTop: '12px' }}>
                {(resumeData.projects || []).map((proj, idx) => (
                  <div key={idx} className="project-tag" style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '8px', marginBottom: '8px' }}>
                    {proj.title} <br />
                    <span>{proj.description}</span> <br />
                    {proj.link && <a href={proj.link} target="_blank" rel="noopener noreferrer">{proj.link}</a>}<br />
                    {proj.year && <span>Year: {proj.year}</span>}<br />
                    {proj.technologies && proj.technologies.length > 0 && <span>Tech: {proj.technologies.join(', ')}</span>}<br />
                    <button type="button" onClick={() => removeProject(idx)} className="remove-skill-btn">Remove</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'education' && (
            <div className="tab-panel">
              <h3 className="section-title">Education</h3>
              {(resumeData.education || []).map((edu, eduIndex) => (
                <div key={eduIndex} className="education-item form-section-item">
                  <div className="form-group-grid">
                    <div className={`form-group ${validationErrors.education?.[eduIndex]?.degree ? 'error' : ''}`}>
                      <label>Degree / Field of Study</label>
                      <input
                        type="text"
                        value={edu.degree || ""}
                        onChange={(e) => updateEducation(eduIndex, "degree", e.target.value)}
                        placeholder="B.A. in Business Administration"
                      />
                      {validationErrors.education?.[eduIndex]?.degree && <p className="error-message">{validationErrors.education[eduIndex].degree}</p>}
                    </div>
                    <div className={`form-group ${validationErrors.education?.[eduIndex]?.institution ? 'error' : ''}`}>
                      <label>Institution</label>
                      <input
                        type="text"
                        value={edu.institution || ""}
                        onChange={(e) => updateEducation(eduIndex, "institution", e.target.value)}
                        placeholder="University of XYZ"
                      />
                      {validationErrors.education?.[eduIndex]?.institution && <p className="error-message">{validationErrors.education[eduIndex].institution}</p>}
                    </div>
                  </div>
                  <div className="form-group-grid">
                    <div className={`form-group ${validationErrors.education?.[eduIndex]?.gpa ? 'error' : ''}`}>
                      <label>GPA (optional)</label>
                      <input
                        type="text"
                        value={edu.gpa || ""}
                        onChange={(e) => updateEducation(eduIndex, "gpa", e.target.value)}
                        placeholder="3.8/4.0"
                      />
                      {validationErrors.education?.[eduIndex]?.gpa && <p className="error-message">{validationErrors.education[eduIndex].gpa}</p>}
                    </div>
                    <div className={`form-group ${validationErrors.education?.[eduIndex]?.graduation_date ? 'error' : ''}`}>
                      <label>Graduation Date</label>
                      <input
                        type="month"
                        value={edu.graduation_date || ""}
                        onChange={(e) => updateEducation(eduIndex, "graduation_date", e.target.value)}
                      />
                      {validationErrors.education?.[eduIndex]?.graduation_date && <p className="error-message">{validationErrors.education[eduIndex].graduation_date}</p>}
                    </div>
                  </div>
                  <button type="button" onClick={() => removeEducation(eduIndex)} className="remove-section-btn">
                    Remove Education
                  </button>
                </div>
              ))}
              <button type="button" onClick={addEducation} className="add-section-btn">
                Add Education
              </button>
            </div>
          )}

          {activeSection === 'training' && (
            <div className="tab-panel">
              <h3 className="section-title">Certifications & Training</h3>
              {(resumeData.training || []).map((train, trainIndex) => (
                <div key={trainIndex} className="training-item form-section-item">
                  <div className="form-group-grid">
                    <div className={`form-group ${validationErrors.training?.[trainIndex]?.title ? 'error' : ''}`}>
                      <label>Course / Certification Name</label>
                      <input
                        type="text"
                        value={train.title || ""}
                        onChange={(e) => updateTraining(trainIndex, "title", e.target.value)}
                        placeholder="Certified Sales Professional"
                      />
                      {validationErrors.training?.[trainIndex]?.title && <p className="error-message">{validationErrors.training[trainIndex].title}</p>}
                    </div>
                    <div className={`form-group ${validationErrors.training?.[trainIndex]?.institute ? 'error' : ''}`}>
                      <label>Issuing Organization</label>
                      <input
                        type="text"
                        value={train.institute || ""}
                        onChange={(e) => updateTraining(trainIndex, "institute", e.target.value)}
                        placeholder="Sales Academy"
                      />
                      {validationErrors.training?.[trainIndex]?.institute && <p className="error-message">{validationErrors.training[trainIndex].institute}</p>}
                    </div>
                  </div>
                  <div className={`form-group ${validationErrors.training?.[trainIndex]?.year ? 'error' : ''}`}>
                    <label>Completion Date</label>
                    <input
                      type="month"
                      value={train.year || ""}
                      onChange={(e) => updateTraining(trainIndex, "year", e.target.value)}
                    />
                    {validationErrors.training?.[trainIndex]?.year && <p className="error-message">{validationErrors.training[trainIndex].year}</p>}
                  </div>
                  <button type="button" onClick={() => removeTraining(trainIndex)} className="remove-section-btn">
                    Remove Training
                  </button>
                </div>
              ))}
              <button type="button" onClick={addTraining} className="add-section-btn">
                Add Certification/Training
              </button>
            </div>
          )}

          {activeSection === 'references' && (
            <div className="tab-panel">
              <h3 className="section-title">References</h3>
              <p className="form-description">List professional references. It's often sufficient to state "References available upon request."</p>
              {(resumeData.reference ? [resumeData.reference] : []).map((ref, refIndex) => (
                <div key={refIndex} className="reference-item form-section-item">
                  <div className="form-group-grid">
                    <div className={`form-group ${validationErrors.references?.[refIndex]?.name ? 'error' : ''}`}>
                      <label>Name</label>
                      <input
                        type="text"
                        value={ref.name || ""}
                        onChange={(e) => updateReference(refIndex, "name", e.target.value)}
                        placeholder="Jane Doe"
                      />
                      {validationErrors.references?.[refIndex]?.name && <p className="error-message">{validationErrors.references[refIndex].name}</p>}
                    </div>
                    <div className={`form-group ${validationErrors.references?.[refIndex]?.designation ? 'error' : ''}`}>
                      <label>Designation</label>
                      <input
                        type="text"
                        value={ref.designation || ""}
                        onChange={(e) => updateReference(refIndex, "designation", e.target.value)}
                        placeholder="Head of Sales"
                      />
                      {validationErrors.references?.[refIndex]?.designation && <p className="error-message">{validationErrors.references[refIndex].designation}</p>}
                    </div>
                  </div>
                  <div className="form-group-grid">
                    <div className={`form-group ${validationErrors.references?.[refIndex]?.company ? 'error' : ''}`}>
                      <label>Company</label>
                      <input
                        type="text"
                        value={ref.company || ""}
                        onChange={(e) => updateReference(refIndex, "company", e.target.value)}
                        placeholder="Global Corp"
                      />
                      {validationErrors.references?.[refIndex]?.company && <p className="error-message">{validationErrors.references[refIndex].company}</p>}
                    </div>
                    <div className={`form-group ${validationErrors.references?.[refIndex]?.email ? 'error' : ''}`}>
                      <label>Email</label>
                      <input
                        type="email"
                        value={ref.email || ""}
                        onChange={(e) => updateReference(refIndex, "email", e.target.value)}
                        placeholder="jane.doe@example.com"
                      />
                      {validationErrors.references?.[refIndex]?.email && <p className="error-message">{validationErrors.references[refIndex].email}</p>}
                    </div>
                  </div>
                  <div className={`form-group ${validationErrors.references?.[refIndex]?.phone ? 'error' : ''}`}>
                    <label>Phone</label>
                    <input
                      type="text"
                      value={ref.phone || ""}
                      onChange={(e) => updateReference(refIndex, "phone", e.target.value)}
                      placeholder="+1234567890"
                    />
                    {validationErrors.references?.[refIndex]?.phone && <p className="error-message">{validationErrors.references[refIndex].phone}</p>}
                  </div>
                  <button type="button" onClick={() => removeReference(refIndex)} className="remove-section-btn">
                    Remove Reference
                  </button>
                </div>
              ))}
              <button type="button" onClick={addReference} className="add-section-btn">
                Add Reference
              </button>
            </div>
          )}

          {activeSection === 'languages' && (
            <div className="tab-panel">
              <h3 className="section-title">Languages</h3>
              {(resumeData.languages || []).map((lang, langIndex) => (
                <div key={langIndex} className="language-item form-section-item">
                  <div className="form-group-grid">
                    <div className={`form-group ${validationErrors.languages?.[langIndex]?.name ? 'error' : ''}`}>
                      <label>Language</label>
                      <input
                        type="text"
                        value={lang.name || ""}
                        onChange={(e) => updateLanguage(langIndex, "name", e.target.value)}
                        placeholder="English"
                        maxLength="50"
                      />
                      {validationErrors.languages?.[langIndex]?.name && <p className="error-message">{validationErrors.languages[langIndex].name}</p>}
                    </div>
                    <div className={`form-group ${validationErrors.languages?.[langIndex]?.proficiency ? 'error' : ''}`}>
                      <label>Proficiency Level (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={lang.proficiency || ""}
                        onChange={(e) => updateLanguage(langIndex, "proficiency", parseInt(e.target.value))}
                        placeholder="85"
                      />
                      {validationErrors.languages?.[langIndex]?.proficiency && <p className="error-message">{validationErrors.languages[langIndex].proficiency}</p>}
                    </div>
                  </div>
                  <button type="button" onClick={() => removeLanguage(langIndex)} className="remove-section-btn">
                    Remove Language
                  </button>
                </div>
              ))}
              <button type="button" onClick={addLanguage} className="add-section-btn">
                Add Language
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessDevResumeForm;
